import React from "react";
import { Copy, Trash2 } from "lucide-react";
import { styles, btnIconGhost } from "./styles";
import { kindLabel, kindStyles } from "./KindBadge";
import { PromptItem, ServerPrompt } from "./types";

type Props = {
  item: PromptItem;
  items: PromptItem[];
  setItems: React.Dispatch<React.SetStateAction<PromptItem[]>>;
  openEditor: (id: string) => void;
  apiPost: <T>(path: string, body: any) => Promise<T>;
  apiDelete: <T>(path: string) => Promise<T>;
  setError: (s: string | null) => void;
  closeEditorIf: (id: string) => void;
  readonly?: boolean;
  /** This summarize prompt is the one the session summarizer runs. */
  isSummarizer?: boolean;
  /** Localized badge label for the active summarizer. */
  summarizerLabel?: string;
};

const PromptTile: React.FC<Props> = ({
  item: it,
  items,
  setItems,
  openEditor,
  apiPost,
  apiDelete,
  setError,
  closeEditorIf,
  readonly,
  isSummarizer,
  summarizerLabel,
}) => {
  return (
    <div
      key={it.id}
      style={styles.tile}
      onClick={() => openEditor(it.id)}
      role="button"
    >
      <div style={styles.tileHead}>
        <div style={styles.tileTitle} title={it.name}>
          {it.name}
        </div>
        {isSummarizer && (
          <span style={kindStyles("summarize")} title={summarizerLabel}>
            ★ {summarizerLabel}
          </span>
        )}
        <span style={kindStyles(it.kind)} title={kindLabel(it.kind)}>
          {kindLabel(it.kind)}
        </span>
      </div>

      <div style={styles.tileBody} title={it.description || "—"}>
        {it.description || "—"}
      </div>

      {!readonly && (
        <div style={styles.tileActions} onClick={(e) => e.stopPropagation()}>
          <button
            style={btnIconGhost()}
            onClick={async (e) => {
              e.preventDefault();
              const src = items.find((p) => p.id === it.id);
              if (!src) return;
              try {
                const r = await apiPost<{ ok: boolean; prompt: ServerPrompt }>(
                  "/prompts",
                  {
                    title: src.name + " (copy)",
                    kind: src.kind,
                    description: src.description,
                    content: src.template,
                    // Keep the clinical-note marker: a duplicated SOAP prompt
                    // must stay a SOAP prompt, not silently become freeform.
                    ...(src.note_format ? { note_format: src.note_format } : {}),
                  }
                );
                const p = r.prompt;
                const copy: PromptItem = {
                  id: p.id,
                  name: p.title,
                  template: p.content,
                  kind: p.kind,
                  description: p.description || "",
                  note_format: p.note_format,
                };
                setItems((prev) => [copy, ...prev]);
                openEditor(copy.id);
              } catch (err: any) {
                setError(err?.message || String(err));
              }
            }}
            title="Duplicate"
            aria-label="Duplicate"
          >
            <Copy size={14} />
          </button>

          <button
            style={btnIconGhost("var(--color-danger)")}
            onClick={async () => {
              try {
                await apiDelete(`/prompts/${encodeURIComponent(it.id)}`);
                setItems((prev) => prev.filter((p) => p.id !== it.id));
                closeEditorIf(it.id);
              } catch (err: any) {
                setError(err?.message || String(err));
              }
            }}
            title="Delete"
            aria-label="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PromptTile;
